import { storage } from "@/lib/storage";
import { MapItemSUP } from "@/types/search";
import { logger } from "@/utils/logger";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface PropertyInteractionState {
	// Property overlay state
	overlaySups: MapItemSUP[] | null;
	overlaySupId: string | null;
	overlayProjectName: string | null;
	isPropertyOverlayOpen: boolean;
	viewedProperties: Set<string>;
	overlayProjectId: string | null;

	// Preference search state - single source of truth for current property index
	currentPropertyIndex: number;
	propertiesCount: number;

	// Property overlay methods
	openPropertyOverlay: ({
		sups,
		initialSupId,
		projectName,
		projectId,
	}: {
		sups: MapItemSUP[];
		initialSupId?: string;
		projectName?: string;
		projectId?: string;
	}) => void;
	updateOverlaySups: (sups: MapItemSUP[]) => void;
	closePropertyOverlay: () => void;
	setSupId: (id: string | null) => void;
	initViewedProperties: (viewedIds: string[]) => void;
	addViewedProperty: (propertyId: string) => void;

	// Preference search methods
	setCurrentPropertyIndex: (index: number) => void;
	setPropertiesCount: (count: number) => void;
}

export const usePropertyInteractionStore = create<PropertyInteractionState>()(
	devtools(
		subscribeWithSelector(
			immer(set => ({
				// Property overlay state
				overlaySups: null,
				overlaySupId: null,
				overlayProjectName: null,
				isPropertyOverlayOpen: false,
				viewedProperties: new Set<string>(),
				overlayProjectId: null,

				// Preference search state
				currentPropertyIndex: 0,
				propertiesCount: 0,

				// Property overlay methods
				openPropertyOverlay: ({ sups, initialSupId, projectName, projectId }) => {
					set(state => {
						state.overlaySups = sups;
						state.overlaySupId = initialSupId || sups[0]?._id || null;
						state.overlayProjectName = projectName || null;
						state.isPropertyOverlayOpen = true;
						state.overlayProjectId = projectId || null;
					});
					logger.debug("property overlay opening", {
						initialSupId,
						projectName,
						projectId,
						supsCount: sups?.length,
					});
				},
				updateOverlaySups: sups =>
					set(state => {
						state.overlaySups = sups;
						// Keep overlaySupId unchanged - just update the available properties
					}),
				closePropertyOverlay: () =>
					set(state => {
						state.isPropertyOverlayOpen = false;
						state.overlaySupId = null;
						state.overlayProjectId = null;
					}),
				setSupId: id =>
					set(state => {
						state.overlaySupId = id;
					}),
				initViewedProperties: viewedIds =>
					set(state => {
						state.viewedProperties = new Set(viewedIds);
					}),
				addViewedProperty: propertyId =>
					set(state => {
						if (propertyId && !state.viewedProperties.has(propertyId)) {
							state.viewedProperties.add(propertyId);

							// Save to storage (IndexedDB)
							if (typeof window !== "undefined") {
								const viewedArray = Array.from(state.viewedProperties);
								storage.setItem("viewed_properties", viewedArray).catch(error => {
									// eslint-disable-next-line no-console
									console.warn("Failed to save viewed properties to storage:", error);
								});
							}
						}
					}),

				// Preference search methods
				setCurrentPropertyIndex: index =>
					set(state => {
						state.currentPropertyIndex = index;
					}),
				setPropertiesCount: count =>
					set(state => {
						state.propertiesCount = count;
					}),
			})),
		),
		{ name: "property-interaction-store" },
	),
);
